import { useTranslation } from 'react-i18next';

import {
    Body1,
    Body2,
    Flex,
    Stack,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';

import type {
    AuthorizedMedicationInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../../graphql/generated/graphql';
import { useMedicationPricing } from '../../../hooks/use-medication-pricing';
import type { PricingInfo } from '../../../util/config/get-pricing';

type MedicationPricingInfoProps = {
    pricingInfo: PricingInfo;
    medication?:
        | AuthorizedMedicationInfoFragment
        | UnknownMedicationInfoFragment;
};

export const MedicationPricingInfo = ({
    pricingInfo,
    medication,
}: MedicationPricingInfoProps) => {
    const { t } = useTranslation('lilly');
    const { price, isOgZepbound, savingsAmount } = useMedicationPricing(
        pricingInfo,
        medication
    );

    const pricing =
        pricingInfo.type === 'selfPay'
            ? {
                  option: t('pricing.cashPrice'),
                  value:
                      savingsAmount === 0
                          ? `${price}*`
                          : t('pricing.cashValue', { price }),
                  details: isOgZepbound ? t('pricing.newLowerPrice') : null,
                  subtext: t('pricingInfo.pricingSubjectToChange'),
              }
            : {
                  option: t('pricing.withInsurance'),
                  value: t('pricingInfo.insuranceValue', { price }),
                  details: t('pricingInfo.usingSavingsCard'),
              };

    return (
        <Flex justify="space-between">
            <Stack gap={0}>
                <Subtitle2>{pricing.option}</Subtitle2>
                <Body1>{pricing.details}</Body1>
                {pricing.subtext ? <Body2>{pricing.subtext}</Body2> : null}
            </Stack>
            <Subtitle2>{pricing.value}</Subtitle2>
        </Flex>
    );
};
